import moment from 'moment'
import { IBillDocument, IRenewableEvidence } from '../services/file-upload/file-upload.model'

/**
 * The bill review's to-do list. Every row still waiting in a bill upload lands in exactly one place:
 * a job the reviewer has to do before it can import (discard a cancelled bill, fill in a figure we
 * could not read, deal with dates outside the reporting period, fix anything else), a possible
 * duplicate to check, or the list of rows ready to import. The review step of the import wizard and
 * the upload's Pending tab both build their page from this, so they always agree.
 */

export type BillTaskKind = 'cancelled' | 'missing' | 'outside-period' | 'fix' | 'duplicate'

export interface IBillReviewGroups {
  cancelled: any[]
  missing: any[]
  outsidePeriod: any[]
  fix: any[]
  duplicates: any[]
  ready: any[]
}

// The import check's field names (the template headings, see the backend's excelTemplate.js).
const CANCELLED_FIELD = 'Bill'
const DATE_RANGE_FIELD = 'Date Range'
// The figures a reviewer can type straight into the row, with the bill open beside it.
const INLINE_FIELDS = ['Units Consumed', 'Start Date', 'End Date']

export const normaliseValidationErrors = (errors: any): any[] => {
  if (Array.isArray(errors)) return errors
  if (typeof errors === 'string') {
    try {
      const parsed = JSON.parse(errors)
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return []
    }
  }
  if (errors && typeof errors === 'object') {
    return Object.values(errors).flat().filter(Boolean)
  }
  return []
}

/**
 * A bill flagged as a possible duplicate, still worth checking. Once every row of the other bill in
 * the same upload has been discarded, the pair is settled and the flag is no longer a job. A match
 * against a bill in an earlier upload always is.
 */
export const isLiveDuplicate = (document: IBillDocument | undefined, documentsById: Record<string, IBillDocument>): boolean => {
  if (!document?.duplicateOfBillDocumentId) return false
  const other = documentsById[document.duplicateOfBillDocumentId]
  if (!other) return true
  return (other.outstandingRowCount || 0) > 0 || (other.committedRowCount || 0) > 0
}

export const billTaskForLine = (line: any, documentsById: Record<string, IBillDocument>): BillTaskKind | 'ready' => {
  const document = line?.billDocumentId ? documentsById[line.billDocumentId] : undefined
  const errors = normaliseValidationErrors(line?.validationErrors)
  const fields: string[] = errors.map((error: any) => error?.field || '')
  if (document?.cancelledByBillDocumentId || fields.includes(CANCELLED_FIELD)) return 'cancelled'
  if (errors.length === 0) {
    if (line?.commitStatus === 'FAILED') return 'fix'
    return isLiveDuplicate(document, documentsById) ? 'duplicate' : 'ready'
  }
  const others = fields.filter((field) => !INLINE_FIELDS.includes(field))
  if (others.length === 0) return 'missing'
  // The range check only runs once both dates are there, so these rows have their dates.
  if (others.every((field) => field === DATE_RANGE_FIELD)) return 'outside-period'
  return 'fix'
}

export const groupBillRows = (lines: any[], documentsById: Record<string, IBillDocument>): IBillReviewGroups => {
  const groups: IBillReviewGroups = { cancelled: [], missing: [], outsidePeriod: [], fix: [], duplicates: [], ready: [] }
  for (const line of lines) {
    const task = billTaskForLine(line, documentsById)
    if (task === 'cancelled') groups.cancelled.push(line)
    else if (task === 'missing') groups.missing.push(line)
    else if (task === 'outside-period') groups.outsidePeriod.push(line)
    else if (task === 'fix') groups.fix.push(line)
    else if (task === 'duplicate') groups.duplicates.push(line)
    else groups.ready.push(line)
  }
  return groups
}

/** How many separate jobs are on the list: one per kind of task with anything in it. */
export const countBillTasks = (groups: IBillReviewGroups): number =>
  [groups.cancelled, groups.missing, groups.outsidePeriod, groups.fix, groups.duplicates].filter((rows) => rows.length > 0).length

export const isElectricityLine = (line: any): boolean => /electricity/i.test(String(line?.categoryName || ''))

/** A row already carrying a 0 supplier rate: 0 market-based, whoever set it. */
export const hasRenewableRate = (line: any): boolean => {
  const rate = line?.customMarketBasedMultiplier
  return rate !== null && rate !== undefined && rate !== '' && Number(rate) === 0
}

/** Evidence the reviewer can confirm in one click: it covers all of the bill's electricity. */
export const coversAllUsage = (evidence?: IRenewableEvidence | null): boolean =>
  !!evidence && typeof evidence.coveragePercent === 'number' && evidence.coveragePercent >= 99.95

export interface IRenewableJob {
  /** Bills whose renewable evidence covers all their electricity, not yet decided. */
  confirmable: IBillDocument[]
  /** Bills whose certificates cover only part of their usage: the rate is set by hand. */
  partial: IBillDocument[]
}

/**
 * The renewable electricity job: bills that say their supply is renewable and backed by certificates,
 * with electricity rows that will import (ready, a possible duplicate, or waiting for a figure), and
 * no decision yet. A cancelled bill or a credit note is never offered: it does not import.
 */
export const findRenewableJob = (documents: IBillDocument[], groups: IBillReviewGroups): IRenewableJob => {
  const rows = [...groups.ready, ...groups.duplicates, ...groups.missing].filter(isElectricityLine)
  const undecided = documents.filter((document) =>
    !!document.renewableEvidence
    && !document.renewableDecision
    && !document.isCreditNote
    && !document.cancelledByBillDocumentId
    && rows.some((line) => line.billDocumentId === document.id))
  return {
    // A bill whose electricity rows all carry a 0 rate already needs nothing.
    confirmable: undecided.filter((document) => coversAllUsage(document.renewableEvidence)
      && rows.some((line) => line.billDocumentId === document.id && !hasRenewableRate(line))),
    partial: undecided.filter((document) => !coversAllUsage(document.renewableEvidence)),
  }
}

/** Distinct bills behind a set of rows: a bill with a gas and an electricity row is one bill. */
export const countBills = (lines: any[]): number => {
  const ids = new Set<string>()
  lines.forEach((line, index) => ids.add(line?.billDocumentId || `row-${index}`))
  return ids.size
}

export const plural = (count: number, one: string, many: string) => `${count.toLocaleString()} ${count === 1 ? one : many}`

/** "51 bills read. 34 rows are ready to import." */
export const billReviewHeadline = (billCount: number, readyCount: number): string => {
  const ready = readyCount === 0
    ? 'No rows are ready to import yet.'
    : `${plural(readyCount, 'row is', 'rows are')} ready to import.`
  return `${plural(billCount, 'bill', 'bills')} read. ${ready}`
}

export interface IInlineValues {
  unitsConsumed: string
  startDate: string | null
  endDate: string | null
}

/**
 * The usage figure as typed or pasted from a bill: "133,800", "133 800" and "133,800 kWh" all read as
 * 133800. UK bills print thousands separators, and a figure copied with them must not look invalid.
 * NaN when there is no number.
 */
export const parseUsage = (text: string): number => {
  const cleaned = text.replace(/[,\s\u00a0]/g, '').replace(/[a-z]+$/i, '')
  return cleaned === '' ? NaN : Number(cleaned)
}

/** What stops an inline save, in words, or null when the values can be saved. */
export const inlineValuesProblem = (values: IInlineValues): string | null => {
  if (values.unitsConsumed.trim() === '') return 'Enter the usage from the bill'
  const usage = parseUsage(values.unitsConsumed)
  if (!Number.isFinite(usage)) return 'Enter the usage as a number'
  if (usage <= 0) return 'The usage must be more than 0'
  const start = values.startDate ? moment(values.startDate) : null
  const end = values.endDate ? moment(values.endDate) : null
  if (!start || !start.isValid()) return 'Enter the start date'
  if (!end || !end.isValid()) return 'Enter the end date'
  if (end.isBefore(start, 'day')) return 'The end date is before the start date'
  return null
}

/**
 * The body for saving a row's usage and dates from the review. The edit endpoint rebuilds the whole
 * row from what it is sent (location, supplier, subsidiary and the rest come from the body, and
 * custom field values are replaced), so every other value is sent back exactly as it was.
 */
export const buildInlineEditPayload = (line: any, values: IInlineValues) => ({
  ruleId: line.ruleId ?? null,
  conversionId: line.conversionId ?? null,
  groupId: line.groupId ?? null,
  categoryId: line.categoryId ?? null,
  capitalGoodId: line.capitalGoodId ?? null,
  customMarketBasedMultiplier: line.customMarketBasedMultiplier ?? null,
  measurementUnitName: line.measurementUnitName ?? null,
  categoryName: line.categoryName ?? null,
  detailLevelOne: line.detailLevelOne ?? null,
  detailLevelTwo: line.detailLevelTwo ?? null,
  detailLevelThree: line.detailLevelThree ?? null,
  detailLevelFour: line.detailLevelFour ?? null,
  detailLevelFive: line.detailLevelFive ?? null,
  subsidiaryId: line.subsidiaryId ?? null,
  countryId: line.countryId ?? null,
  locationId: line.locationId ?? null,
  vehicleId: line.vehicleId ?? null,
  projectId: line.projectId ?? null,
  contactSupplierId: line.contactSupplierId ?? null,
  regulatedEmissionTradingSchemeId: line.regulatedEmissionTradingSchemeId ?? null,
  isFlag: line.isFlag === true,
  flagCategory: line.flagCategory ?? null,
  customFieldValues: (line.customFieldValues || []).map((value: any) => ({ customFieldId: value.customFieldId, value: value.value ?? null })),
  unitsConsumed: parseUsage(values.unitsConsumed),
  startDate: moment(values.startDate).format('YYYY-MM-DD'),
  endDate: moment(values.endDate).format('YYYY-MM-DD'),
})
